import React, { useEffect, useState } from "react";
import { Megaphone, X, Play, Stop } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { announce, playAnnouncement, stopAnnouncement, AUTO_ANNOUNCE_KEY, ANNOUNCER_VOICE_KEY } from "../lib/announcer";

/**
 * AnnouncerPanel — host-side AI announcer for live streams. Generates an
 * ElevenLabs clip from a preset (battle start, host intro, winner…) and plays
 * it into the outgoing stream so viewers hear it.
 */
export default function AnnouncerPanel({ open, onClose, sessionId }) {
  const [presets, setPresets] = useState([]);
  const [voices, setVoices] = useState([]);
  const [presetId, setPresetId] = useState("host_intro");
  const [fields, setFields] = useState({});
  const [voiceId, setVoiceId] = useState(localStorage.getItem(ANNOUNCER_VOICE_KEY) || "pNInz6obpgDQGcFmaJgB");
  const [auto, setAuto] = useState(localStorage.getItem(AUTO_ANNOUNCE_KEY) !== "0");
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/ai/voice/announcer/presets").then((r) => setPresets(r.data.presets)).catch(() => {});
    api.get("/ai/voice/voices").then((r) => setVoices(r.data.voices.filter((v) => !v.is_premium || r.data.is_premium_user))).catch(() => {});
    api.get("/ai/voice/audio", { params: { kind: "announcer", limit: 6 } }).then((r) => setRecent(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => { localStorage.setItem(ANNOUNCER_VOICE_KEY, voiceId); }, [voiceId]);
  useEffect(() => { localStorage.setItem(AUTO_ANNOUNCE_KEY, auto ? "1" : "0"); }, [auto]);

  if (!open) return null;
  const preset = presets.find((p) => p.id === presetId);

  const go = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const rec = await announce(presetId, fields, sessionId);
      setRecent((r) => [rec, ...r].slice(0, 6));
      toast.success("Announcement is playing to your viewers 📣");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Announcer failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-end" data-testid="announcer-panel">
      <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-h-[85vh] overflow-y-auto bg-ink rounded-t-3xl border-t border-white/10 p-5 animate-fade-up">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Live · ElevenLabs</p>
            <h3 className="text-cream text-2xl font-black tracking-tight flex items-center gap-2"><Megaphone size={22} weight="fill" className="text-papaya" /> AI Announcer</h3>
          </div>
          <button onClick={onClose} data-testid="announcer-close" className="p-2 rounded-full hover:bg-white/5"><X size={22} className="text-cream" /></button>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="announcer-presets">
          {presets.map((p) => (
            <button key={p.id} onClick={() => { setPresetId(p.id); setFields({}); }} data-testid={`announcer-preset-${p.id}`} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.15em] uppercase border ${presetId === p.id ? "bg-papaya text-ink border-papaya" : "bg-white/5 text-cream/70 border-white/10"}`}>
              {p.emoji} {p.label}
            </button>
          ))}
        </div>

        {preset && (
          <div className="mt-3 space-y-2">
            {preset.fields.map((f) => (
              f.key === "script" ? (
                <textarea key={f.key} rows={3} value={fields[f.key] || ""} onChange={(e) => setFields({ ...fields, [f.key]: e.target.value.slice(0, 400) })} placeholder={f.placeholder} data-testid={`announcer-field-${f.key}`} className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-2.5 text-cream placeholder:text-cream/30 focus:outline-none focus:border-papaya text-sm" />
              ) : (
                <input key={f.key} value={fields[f.key] || ""} onChange={(e) => setFields({ ...fields, [f.key]: e.target.value.slice(0, 120) })} placeholder={`${f.label} · e.g. ${f.placeholder}`} data-testid={`announcer-field-${f.key}`} className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-2.5 text-cream placeholder:text-cream/30 focus:outline-none focus:border-papaya text-sm" />
              )
            ))}
            <p className="text-cream/40 text-[11px] italic leading-relaxed" data-testid="announcer-script-preview">
              “{preset.fields.reduce((t, f) => t.replace(`{${f.key}}`, fields[f.key] || f.placeholder), preset.template)}”
            </p>
          </div>
        )}

        <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="announcer-voices">
          {voices.map((v) => (
            <button key={v.voice_id} onClick={() => setVoiceId(v.voice_id)} data-testid={`announcer-voice-${v.voice_id}`} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.15em] uppercase border ${voiceId === v.voice_id ? "bg-jam-gold text-jam-black border-jam-gold" : "bg-white/5 text-cream/70 border-white/10"}`}>
              {v.name}
            </button>
          ))}
        </div>

        <div className="mt-4 flex items-center gap-2">
          <button onClick={go} disabled={busy} data-testid="announcer-go" className="flex-1 py-3.5 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-xs flex items-center justify-center gap-2 disabled:opacity-40">
            <Megaphone size={16} weight="fill" /> {busy ? "Generating…" : "Announce now"}
          </button>
          <button onClick={stopAnnouncement} data-testid="announcer-stop" title="Stop" className="w-12 h-12 rounded-full crystal-glass text-cream flex items-center justify-center"><Stop size={18} weight="fill" /></button>
        </div>

        <label className="mt-4 flex items-center justify-between p-3 rounded-2xl bg-white/5 border border-white/5">
          <span className="text-cream text-sm font-bold">Auto-announce gift battles</span>
          <button onClick={() => setAuto((a) => !a)} data-testid="announcer-auto-toggle" aria-pressed={auto} className={`w-11 h-6 rounded-full relative transition-colors ${auto ? "bg-papaya" : "bg-white/15"}`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-ink transition-all ${auto ? "left-[22px]" : "left-0.5"}`} />
          </button>
        </label>

        {recent.length > 0 && (
          <div className="mt-4">
            <p className="text-cream/50 text-[10px] tracking-[0.25em] uppercase font-bold mb-2">Replay recent</p>
            <div className="space-y-1.5" data-testid="announcer-recent">
              {recent.map((a) => (
                <button key={a.id} onClick={() => playAnnouncement(a.url).catch(() => {})} data-testid={`announcer-replay-${a.id}`} className="w-full flex items-center gap-2 p-2.5 rounded-2xl bg-white/5 border border-white/5 text-left">
                  <Play size={14} weight="fill" className="text-papaya flex-shrink-0" />
                  <span className="text-cream text-xs truncate">{a.text}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
